/**
 * DG Predictive Search
 * Shopify Predictive Search — section Liquid filtrée par Wholesale Lock Manager
 * ThemeDGCore | Distribution Gauthier
 *
 * Particuliers (window.dgSearch.audience === 'b2c') : la requête porte
 * « tag:_B2C », sinon Shopify renvoie surtout des produits entreprises que WLM
 * masque ensuite, et la liste reste vide. WLM demeure le filtre de sécurité.
 */
(function () {
    'use strict';

    const DEBOUNCE_MS = 260;
    const FETCH_LIMIT = 10;   // maximum Shopify; la section en affiche 6
    const MIN_CHARS   = 2;
    const API_BASE    = window.routes?.predictive_search_url || '/search/suggest';
    const SEARCH_URL  = window.routes?.search_url || '/search';
    const RETAIL      = !!(window.dgSearch && window.dgSearch.audience === 'b2c');
    const RETAIL_TAG  = '_B2C';

    /* ── Utilities ─────────────────────────────────────────────────── */
    function esc(str) {
        return String(str || '').replace(/[&<>"']/g, c =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
        );
    }

    function formatPrice(priceStr) {
        const val = parseFloat(priceStr);
        if (isNaN(val) || val === 0) return '';
        return val.toFixed(2).replace('.', ',') + ' $';
    }

    function debounce(fn, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    /** Code produit comparable : « dg 130 », « DG-130 » → « DG130 ». */
    function normCode(str) {
        return String(str || '').toUpperCase().replace(/[\s-]+/g, '');
    }

    // « creme » doit surligner « CRÈME », « boeuf » doit surligner « BŒUF ».
    const ACCENTS = { a: 'aàâäá', c: 'cç', e: 'eéèêë', i: 'iîïí', o: 'oôöó', u: 'uùûüú', y: 'yÿ', n: 'nñ' };
    function highlightPattern(query) {
        const q = query.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
        let out = '';
        for (let i = 0; i < q.length; i++) {
            const ch = q[i];
            if ((ch === 'o' || ch === 'a') && q[i + 1] === 'e') {
                out += ch === 'o' ? '(?:oe|œ)' : '(?:ae|æ)';
                i++;
            } else if (ch === 'œ' || ch === 'æ') {
                out += ch === 'œ' ? '(?:oe|œ)' : '(?:ae|æ)';
            } else if (ACCENTS[ch]) {
                out += '[' + ACCENTS[ch] + ']';
            } else {
                out += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            }
        }
        return out;
    }

    function highlight(text, query) {
        if (!query) return esc(text);
        let re;
        try { re = new RegExp(highlightPattern(query), 'gi'); } catch (e) { return esc(text); }
        let html = '';
        let last = 0;
        const src = String(text || '');
        src.replace(re, (match, offset) => {
            if (!match) return match;
            html += esc(src.slice(last, offset)) + '<mark class="dg-predict__hl">' + esc(match) + '</mark>';
            last = offset + match.length;
            return match;
        });
        return html + esc(src.slice(last));
    }

    function withParam(url, key, value) {
        return url + (url.indexOf('?') >= 0 ? '&' : '?') + key + '=' + encodeURIComponent(value);
    }

    /** URL de la page de résultats : mêmes champs que le formulaire du champ. */
    function searchPageUrl(input, query) {
        const form = input.form;
        const parts = ['q=' + encodeURIComponent(query)];
        if (form) {
            form.querySelectorAll('input[type="hidden"][name]').forEach(field => {
                parts.push(encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value));
            });
            return (form.getAttribute('action') || SEARCH_URL) + '?' + parts.join('&');
        }
        parts.push('type=product', 'options%5Bprefix%5D=last');
        if (RETAIL) parts.push('filter.p.tag=' + RETAIL_TAG);
        return SEARCH_URL + '?' + parts.join('&');
    }

    /** Produit dont un format porte exactement ce code : URL du bon format. */
    function exactCodeUrl(products, query) {
        const code = normCode(query);
        if (!/^DG\d+$/.test(code)) return null;
        for (const p of products) {
            const skus = (p.skus || '').split('|');
            const ids = (p.variants || '').split('|');
            const idx = skus.findIndex(s => normCode(s) === code);
            if (idx >= 0 && p.url) return ids[idx] ? withParam(p.url, 'variant', ids[idx]) : p.url;
        }
        return null;
    }

    /* ── Per-input controller ──────────────────────────────────────── */
    function initPredict(input, dropdown) {
        // Abort controller scopes all listeners for this input instance
        var ac = new AbortController();
        var signal = ac.signal;
        let currentQuery = '';
        let activeIdx    = -1;
        let abortCtrl    = null;
        let loaded       = { query: '', products: [] };

        // ⚡ Move dropdown to <body> so position:fixed is relative to viewport,
        //    not to a potentially-transformed header stacking context
        document.body.appendChild(dropdown);

        /* ── Helpers ── */
        function getItems() {
            return dropdown.querySelectorAll('[data-predict-item]');
        }

        function setActive(idx) {
            const items = getItems();
            items.forEach(el => el.classList.remove('is-active'));
            activeIdx = Math.max(-1, Math.min(idx, items.length - 1));
            if (activeIdx >= 0) {
                items[activeIdx].classList.add('is-active');
                items[activeIdx].scrollIntoView({ block: 'nearest' });
            }
        }

        // Position dropdown directly aligned below the search input
        function positionDropdown() {
            const rect = input.getBoundingClientRect();
            dropdown.style.top   = `${rect.bottom + 4}px`;
            dropdown.style.left  = `${rect.left}px`;
            dropdown.style.width = `${rect.width}px`;
        }

        function close() {
            dropdown.hidden = true;
            dropdown.setAttribute('aria-hidden', 'true');
            activeIdx = -1;
        }

        function open() {
            positionDropdown();
            dropdown.hidden = false;
            dropdown.setAttribute('aria-hidden', 'false');
        }

        /* ── Render results ── */
        function group(label, links) {
            if (!links.length) return '';
            return `<div class="dg-predict__group"><span class="dg-predict__group-label">${label}</span>` +
                // <span> : dans un inline-flex, l'espace après <mark> disparaîtrait.
                links.map(l => `<a href="${esc(l.url)}" class="dg-predict__chip" data-predict-item tabindex="-1"><span>${highlight(l.title, currentQuery)}</span></a>`).join('') +
                `</div>`;
        }

        function render(results) {
            const { products, brands, collections } = results;
            let html = group('Marques', brands) + group('Catégories', collections);

            html += products.map(p => {
                const imgSrc  = p.featured_image?.url || p.image || '';
                const imgHtml = imgSrc
                    ? `<img src="${esc(imgSrc)}" alt="${esc(p.title)}" class="dg-predict__img" width="52" height="52" loading="lazy">`
                    : `<div class="dg-predict__img-placeholder"></div>`;

                const price = formatPrice(p.price);
                const avail = p.available === false
                    ? `<span class="dg-predict__badge">Épuisé</span>` : '';
                const code  = (p.skus || '').split('|').filter(Boolean)[0] || '';
                const meta  = [code ? highlight(code, currentQuery) : '', p.vendor ? highlight(p.vendor, currentQuery) : '']
                    .filter(Boolean).join(' · ') || esc(p.type || '');

                return `<a href="${esc(p.url)}" class="dg-predict__item" role="option" data-predict-item tabindex="-1">
                    <div class="dg-predict__img-wrap">${imgHtml}</div>
                    <div class="dg-predict__info">
                        <span class="dg-predict__title">${highlight(p.title, currentQuery)}</span>
                        ${meta ? `<span class="dg-predict__type">${meta}</span>` : ''}
                    </div>
                    <div class="dg-predict__right">
                        ${price ? `<span class="dg-predict__price">${price}</span>` : ''}
                        ${avail}
                    </div>
                </a>`;
            }).join('');

            if (!products.length) {
                html += `<p class="dg-predict__empty">Aucun produit trouvé pour « ${esc(currentQuery)} ».</p>`;
            }
            dropdown.innerHTML = html;

            const seeAll = document.createElement('a');
            seeAll.href      = searchPageUrl(input, currentQuery);
            seeAll.className = 'dg-predict__see-all';
            seeAll.setAttribute('tabindex', '-1');
            seeAll.innerHTML =
                `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>` +
                ` Voir tous les résultats pour <em>« ${esc(currentQuery)} »</em>`;
            dropdown.appendChild(seeAll);

            open();
            setActive(-1);
        }

        /* ── Fetch suggestions ── */
        const fetchSuggest = debounce(async function (query) {
            if (!query || query.length < MIN_CHARS) {
                dropdown.hidden = true;
                return;
            }
            if (input.value.trim() !== query) return;
            currentQuery = query;

            if (abortCtrl) abortCtrl.abort();
            abortCtrl = new AbortController();

            try {
                const shopifyQuery = RETAIL ? `${query} tag:${RETAIL_TAG}` : query;
                const url =
                    `${API_BASE}?q=${encodeURIComponent(shopifyQuery)}` +
                    `&resources[type]=product` +
                    `&resources[limit]=${FETCH_LIMIT}` +
                    `&resources[options][unavailable_products]=last` +
                    `&section_id=dg-predictive-search`;
                const res = await fetch(url, { signal: abortCtrl.signal, cache: 'no-store' });
                if (!res.ok) throw new Error('Predictive search unavailable');
                const html = await res.text();
                if (input.value.trim() !== query) return;
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const results = doc.querySelector('[data-dg-predict-results]');
                if (!results || html.includes('Liquid error')) throw new Error('Invalid search section');
                const products = Array.from(results.querySelectorAll('[data-dg-predict-product]'), el => ({
                    title: el.dataset.title,
                    url: el.dataset.url,
                    image: el.dataset.image,
                    price: el.dataset.price,
                    available: el.dataset.available === 'true',
                    type: el.dataset.type,
                    vendor: el.dataset.vendor,
                    skus: el.dataset.skus,
                    variants: el.dataset.variants
                }));
                const links = sel => Array.from(results.querySelectorAll(sel), el => ({ title: el.dataset.title, url: el.dataset.url }))
                    .filter(l => l.title && l.url);
                loaded = { query, products };
                render({ products, brands: links('[data-dg-predict-brand]'), collections: links('[data-dg-predict-collection]') });
            } catch (e) {
                if (e.name !== 'AbortError') dropdown.hidden = true;
            }
        }, DEBOUNCE_MS);

        /* ── Events ── */

        // Focus: re-trigger results if query exists
        //        Delay reposition so it fires AFTER the spring animation settles
        input.addEventListener('focus', function () {
            setTimeout(positionDropdown, 340);   // wait for 320ms burger collapse
            const q = input.value.trim();
            if (q.length >= MIN_CHARS) fetchSuggest(q);
        });

        // Typing
        input.addEventListener('input', function () {
            if (abortCtrl) abortCtrl.abort();
            close();
            const q = this.value.trim();
            if (!q || q.length < MIN_CHARS) {
                dropdown.hidden = true;
                return;
            }
            fetchSuggest(q);
        });

        // Keyboard navigation
        input.addEventListener('keydown', function (e) {
            switch (e.key) {
                case 'ArrowDown':
                    if (!dropdown.hidden) {
                        e.preventDefault();
                        setActive(activeIdx + 1);
                    }
                    break;
                case 'ArrowUp':
                    if (!dropdown.hidden) {
                        e.preventDefault();
                        if (activeIdx <= 0) setActive(-1);
                        else setActive(activeIdx - 1);
                    }
                    break;
                case 'Enter': {
                    const items = getItems();
                    if (!dropdown.hidden && activeIdx >= 0 && items[activeIdx]) {
                        e.preventDefault();
                        window.location.href = items[activeIdx].href;
                        break;
                    }
                    // Code DG exact (ex. DG130) : directement à la fiche du bon format.
                    // Si les suggestions ne sont pas encore arrivées, la page de
                    // résultats fait la même redirection (dg-search.js).
                    const q = input.value.trim();
                    const direct = loaded.query === q ? exactCodeUrl(loaded.products, q) : null;
                    if (direct) {
                        e.preventDefault();
                        window.location.href = direct;
                    }
                    break;
                }
                case 'Escape':
                    close();
                    input.blur();
                    break;
            }
        });

        // Prevent blur before click registers on dropdown
        dropdown.addEventListener('mousedown', e => e.preventDefault());

        // Blur: close after short delay so item clicks register
        input.addEventListener('blur', function () {
            setTimeout(close, 200);
        });

        // Hover: highlight items
        dropdown.addEventListener('mousemove', function (e) {
            const item = e.target.closest('[data-predict-item]');
            if (item) {
                const idx = Array.prototype.indexOf.call(getItems(), item);
                if (idx >= 0) setActive(idx);
            }
        });

        dropdown.addEventListener('mouseleave', () => setActive(-1));

        // Reposition on resize — scoped to this instance via AbortController
        window.addEventListener('resize', function () {
            if (!dropdown.hidden) positionDropdown();
        }, { signal: signal });

        // Extend close() to abort all listeners when the dropdown is destroyed
        var _originalClose = close;
        close = function () {
            _originalClose();
        };
    }

    /* ── Bootstrap ─────────────────────────────────────────────────── */
    // WeakSet tracks which inputs are already bound — prevents stacking listeners
    // if Shopify re-renders the header section in the theme editor.
    var _boundInputs = new WeakSet();

    function init() {
        document.querySelectorAll('[data-predict-target]').forEach(input => {
            if (_boundInputs.has(input)) return; // already initialized, skip
            const id       = input.dataset.predictTarget;
            const dropdown = document.getElementById(id);
            if (dropdown) {
                initPredict(input, dropdown);
                _boundInputs.add(input);
            }
        });
    }

    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', init)
        : init();

    // Theme editor: re-scan for new inputs after a section re-render
    document.addEventListener('shopify:section:load', init);

    // Exposé pour la page de résultats (dg-search.js) et les tests.
    window.dgSearchExactCodeUrl = exactCodeUrl;
})();
